import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ConditionBuilder } from '../condition-builder/ConditionBuilder';
import { useKeyboardSave } from './use-keyboard-save';
import type { SingleValueRuleElement, Condition, RegoValue } from '@/types/policy-editor';

interface SingleValueRuleFormProps {
    element: SingleValueRuleElement | null;
    onSave: (element: SingleValueRuleElement) => void;
    onCancel: () => void;
}

type ValueType = 'true' | 'false' | 'string' | 'number' | 'reference' | 'object';

export function SingleValueRuleForm({ element, onSave, onCancel }: SingleValueRuleFormProps) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [isDefault, setIsDefault] = useState(false);
    const [valueType, setValueType] = useState<ValueType>('true');
    const [valueText, setValueText] = useState('');
    const [conditions, setConditions] = useState<Condition[]>([]);

    useEffect(() => {
        if (element) {
            setName(element.name);
            setDescription(element.description ?? '');
            setIsDefault(element.isDefault ?? false);
            setConditions(element.conditions);
            const { type, text } = decomposeValue(element.value);
            setValueType(type);
            setValueText(text);
        } else {
            setName(''); setDescription(''); setIsDefault(false);
            setValueType('true'); setValueText(''); setConditions([]);
        }
    }, [element]);

    const handleSave = useCallback(() => {
        if (!name.trim()) return;
        onSave({
            id: element?.id ?? `el-${Date.now()}`,
            elementType: 'singleValueRule',
            name,
            description: description || undefined,
            value: composeValue(valueType, valueText),
            conditions,
            isDefault,
        });
    }, [element, name, description, valueType, valueText, conditions, isDefault, onSave]);

    useKeyboardSave(handleSave, !!name.trim());

    return (
        <div className="flex h-full flex-col">
            <div className="flex-1 space-y-4 overflow-y-auto p-4">
                <div className="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-700">
                    A single-value rule produces one result value when its conditions are met.
                    Use it for boolean flags (allow/deny), string results, or computed values.
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <Label htmlFor="rule-name">Rule name</Label>
                        <Input id="rule-name" placeholder="e.g. allow, resolved_profile_name" value={name} onChange={(e) => setName(e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="rule-desc">Description</Label>
                        <Input id="rule-desc" placeholder="What this rule decides" value={description} onChange={(e) => setDescription(e.target.value)} />
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <Switch id="rule-default" checked={isDefault} onCheckedChange={setIsDefault} />
                    <Label htmlFor="rule-default" className="text-sm">Default value (fallback when no conditions match)</Label>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <Label>Value type</Label>
                        <Select value={valueType} onValueChange={(v) => setValueType(v as ValueType)}>
                            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="true">True</SelectItem>
                                <SelectItem value="false">False</SelectItem>
                                <SelectItem value="string">String</SelectItem>
                                <SelectItem value="number">Number</SelectItem>
                                <SelectItem value="reference">Reference</SelectItem>
                                <SelectItem value="object">Object (JSON)</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    {valueType !== 'true' && valueType !== 'false' && (
                        <div className="space-y-1.5">
                            <Label htmlFor="rule-value">Value</Label>
                            <Input id="rule-value" className="font-mono text-xs" placeholder={valuePlaceholder(valueType)} value={valueText} onChange={(e) => setValueText(e.target.value)} />
                        </div>
                    )}
                </div>

                {!isDefault && <ConditionBuilder conditions={conditions} onChange={setConditions} />}
            </div>

            <div className="flex items-center justify-between border-t px-4 py-3">
                <span className="text-[10px] text-slate-400">Ctrl+Enter to save</span>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
                    <Button size="sm" onClick={handleSave} disabled={!name.trim()}>Save</Button>
                </div>
            </div>
        </div>
    );
}

function valuePlaceholder(type: ValueType): string {
    switch (type) {
        case 'string': return '"some text"';
        case 'number': return '42';
        case 'reference': return 'tier_config.default_profile';
        case 'object': return '{"allow": true}';
        default: return '';
    }
}

function decomposeValue(val: RegoValue): { type: ValueType; text: string } {
    if (val.kind === 'scalar') {
        if (val.value === true) return { type: 'true', text: '' };
        if (val.value === false) return { type: 'false', text: '' };
        if (typeof val.value === 'string') return { type: 'string', text: val.value };
        if (typeof val.value === 'number') return { type: 'number', text: String(val.value) };
    }
    if (val.kind === 'reference') return { type: 'reference', text: val.path };
    if (val.kind === 'raw') return { type: 'object', text: val.text };
    if (val.kind === 'object' || val.kind === 'array') return { type: 'object', text: JSON.stringify(val, null, 2) };
    return { type: 'true', text: '' };
}

function composeValue(type: ValueType, text: string): RegoValue {
    switch (type) {
        case 'true': return { kind: 'scalar', value: true };
        case 'false': return { kind: 'scalar', value: false };
        case 'string': return { kind: 'scalar', value: text };
        case 'number': return { kind: 'scalar', value: Number(text) || 0 };
        case 'reference': return { kind: 'reference', path: text };
        case 'object': return { kind: 'raw', text };
    }
}
